// ── Game Init ── Initialization, lifecycle, and player body creation helpers
// Extracted from Game class — each function takes a GameContext to access/mutate game state.

import * as THREE from 'three';
import { RemotePlayer } from './gameplay/player';
import { audioManager } from './audio/audio-manager';
import { skinRegistry } from './cosmetics/skin-registry';
import { buildSkinMesh } from './cosmetics/skin-builders';
import { type GameContext } from './game-types';
import { updateMoneyDisplay } from './game-input-handlers';
import { attachTPPWeapon } from './gameplay/tpp-weapon';
import { voiceChat } from './audio/voice';
import { voiceInput } from './input/voice-input';

export async function startGame(
  ctx: GameContext,
  enterOfflineModeFn: () => void,
): Promise<void> {
  if (ctx.state !== 'menu') return;
  ctx.state = 'connecting';

  // Capture selected skin before hiding menu
  const selectedSkinId = ctx.skinSelector.selectedSkinId;
  ctx.localPlayer.skinId = selectedSkinId;

  ctx.mainMenu.hide();
  ctx.skinSelector.hide();
  ctx.loadingScreen.show();

  // Try to connect to server with a short timeout
  let connected = false;
  try {
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), 2000),
    );
    await Promise.race([ctx.connection.connect(), timeout]);
    connected = true;
    // Acquire microphone for voice chat (non-blocking — don't fail game start)
    voiceChat.acquireMicrophone().catch(() => {
      console.warn('[Voice] Microphone acquisition failed — voice chat unavailable');
    });
    const name = 'Player' + Math.floor(Math.random() * 999);
    const joinData: Record<string, string | number | null> = { name, skinId: selectedSkinId };
    const gameMode = ctx.connection.getGameMode();
    const mapId = ctx.connection.getMapId();
    if (gameMode) joinData.mode = gameMode;
    if (mapId) joinData.map = mapId;
    ctx.connection.send({ type: 'join', data: joinData });
    ctx.localPlayer.name = name;
  } catch {
    // Server unavailable — enter offline/solo mode
  }

  ctx.loadingScreen.hide();

  if (!connected) {
    enterOfflineModeFn();
  }
}

/** Enter the map in solo/offline mode (no server needed). */
export function enterOfflineMode(
  ctx: GameContext,
  createLocalPlayerBodyFn: () => void,
  spawnOfflineBotFn: () => void,
): void {
  ctx.localPlayer.id = 1;
  ctx.localPlayer.name = 'Player';
  ctx.localPlayer.respawn(0, 0, 60);
  ctx.loadingScreen.hide();
  ctx.state = 'playing';
  voiceInput.enable();
  ctx.hud.show();
  ctx.minimap.show();
  ctx.roundScore.show();
  ctx.roundScore.update(0, 0, 1, 600);
  updateMoneyDisplay(ctx);
  // Pointer lock activates on next click (camera._onClick handler)
  audioManager.play('round_start');

  // Create the local player's body mesh (visible when looking down)
  createLocalPlayerBodyFn();

  // Spawn a bot in front of the player showing the selected skin
  spawnOfflineBotFn();
}

/** Spawn a bot with the player's selected skin so they can see it in-game. */
export function spawnOfflineBot(ctx: GameContext): void {
  const skinId = ctx.localPlayer.skinId;
  const skinDef = skinRegistry.resolveAny(skinId);
  const bot = new RemotePlayer();
  bot.id = 999;
  bot.name = 'Your Skin';
  bot.health = 100;
  bot.createFromSkin(ctx.scene, skinDef.id, ctx.skinLoader, skinDef.fallbackColor, ctx.weaponSkinLoader);
  bot.equipWeapon(
    ctx.localPlayer.currentWeapon,
    ctx.weaponLoadout.equipped[ctx.localPlayer.currentWeapon] ?? 0,
  );
  // Place bot 3 units in front of spawn (player faces -Z by default)
  const bx = ctx.localPlayer.position.x;
  const by = ctx.localPlayer.position.y;
  const bz = ctx.localPlayer.position.z - 3;
  bot.updateFromServer({ x: bx, y: by, z: bz, yaw: Math.PI, pitch: 0, timestamp: performance.now() });
  bot.updateFromServer({ x: bx, y: by, z: bz, yaw: Math.PI, pitch: 0, timestamp: performance.now() + 1 });
  ctx.remotePlayers.set(999, bot);
}

/**
 * Create a first-person body mesh for the local player.
 * Visible when looking down — uses the same buildSkinMesh as the selector.
 */
export function createLocalPlayerBody(ctx: GameContext): void {
  // Remove previous body if any
  if (ctx.localPlayerBody) {
    ctx.scene.remove(ctx.localPlayerBody);
    ctx.localPlayerBody.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
    ctx.localPlayerBody = null;
  }

  const skinId = ctx.localPlayer.skinId;
  const skinDef = skinRegistry.resolveAny(skinId);

  // Prefer the Phase 3 GLB so TPP view, shadow, spectator cam, death cam, and
  // replay see the same hero-prop character a remote viewer sees. Fall back to
  // the procedural builder if the GLB isn't preloaded (shouldn't happen with
  // SkinLoader.preloadAll awaited at boot, but guard for safety).
  const cloned = ctx.skinLoader.cloneSkin(skinDef.id);
  const body = cloned ?? buildSkinMesh(skinDef.id, skinDef.fallbackColor);
  body.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });

  // Wrap in a group for positioning
  const wrapper = new THREE.Group();
  wrapper.add(body);
  // Mark all children as local player so hitscan raycaster skips them
  wrapper.traverse(c => { c.userData.isLocalPlayer = true; });
  ctx.scene.add(wrapper);
  ctx.localPlayerBody = wrapper;

  // Attach current weapon model for TPP view
  attachTPPWeapon(wrapper, ctx.localPlayer.currentWeapon, {
    skinId: ctx.weaponLoadout.equipped[ctx.localPlayer.currentWeapon] ?? 0,
    skinLoader: ctx.weaponSkinLoader,
    stickers: ctx.weaponLoadout.stickers?.[ctx.localPlayer.currentWeapon],
    nameTag: ctx.weaponLoadout.nameTags?.[ctx.localPlayer.currentWeapon],
  });

  // Also update the viewmodel arm colors to match the skin
  ctx.viewmodel.setSkinColors(skinDef.fallbackColor);
}
